import DownloadIcon from '@mui/icons-material/Download';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PermDeviceInformationIcon from '@mui/icons-material/PermDeviceInformation';
import SendIcon from '@mui/icons-material/Send';
import { Menu, Divider } from '@mui/material';
import type { MenuProps } from '@mui/material/Menu';
import { CardMenuItem } from './CardMenuItem';
import { CardMenuItemIcon } from './CardMenuItemIcon';
import { CardMenuItemText } from './CardMenuItemText';

export type CardMenuProps = {
  recommend?: CardMenuItemProps;
  appDetails?: CardMenuItemProps;
  favourites?: CardMenuItemProps & {
    favouritedLabel: string;
  };
  download?: CardMenuItemProps;
  isAppFavourited?: boolean;
  isProUser?: boolean;
};

type CardMenuItemProps = {
  onClick: () => void;
  label: string;
};

type Props = CardMenuProps & MenuProps;

/**
 * Props for configuring a card menu component from [MicroLibraryCoreUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryCoreUI)
 *
 * @property {CardMenuProps} props - The props for the card menu.
 */

export const CardMenu = (props: Props) => {
  const {
    isAppFavourited,
    isProUser,
    recommend,
    favourites,
    download,
    appDetails,
    ...rest
  } = props;

  return (
    <>
      <Menu
        {...rest}
        data-testid="app-card-menu"
        MenuListProps={{
          sx: {
            paddingTop: '15px',
            paddingBottom: '15px',
          },
        }}
      >
        {recommend && (
          <CardMenuItem
            onClick={recommend?.onClick}
            data-testid="app-card-menu-recommend-app"
          >
            <CardMenuItemIcon>
              <SendIcon />
            </CardMenuItemIcon>
            <CardMenuItemText>{recommend.label}</CardMenuItemText>
          </CardMenuItem>
        )}

        {appDetails && (
          <CardMenuItem
            onClick={appDetails.onClick}
            data-testid="app-card-menu-app-details"
          >
            <CardMenuItemIcon>
              <PermDeviceInformationIcon />
            </CardMenuItemIcon>
            <CardMenuItemText>{appDetails.label}</CardMenuItemText>
          </CardMenuItem>
        )}

        {(favourites || download) && <Divider light />}

        {favourites && isProUser && (
          <CardMenuItem
            onClick={favourites.onClick}
            data-testid="app-card-menu-favourites"
            data-favourite-status={
              isAppFavourited ? 'favourited' : 'not-favourited'
            }
          >
            {isAppFavourited ? (
              <>
                <CardMenuItemIcon>
                  <FavoriteIcon />
                </CardMenuItemIcon>
                <CardMenuItemText>
                  {favourites.favouritedLabel}
                </CardMenuItemText>
              </>
            ) : (
              <>
                <CardMenuItemIcon>
                  <FavoriteBorderIcon />
                </CardMenuItemIcon>
                <CardMenuItemText>{favourites.label}</CardMenuItemText>
              </>
            )}
          </CardMenuItem>
        )}

        {download && (
          <CardMenuItem
            onClick={download.onClick}
            data-testid="app-card-menu-download-app"
          >
            <CardMenuItemIcon>
              <DownloadIcon />
            </CardMenuItemIcon>
            <CardMenuItemText>{download.label}</CardMenuItemText>
          </CardMenuItem>
        )}
      </Menu>
    </>
  );
};
